import React, { useCallback, useEffect } from "react";
import { useHistory } from "react-router-dom";
import { get, isEmpty } from "lodash";
import { withTranslation, useTranslation } from "react-i18next";
import { usePreCheckInContext } from "./contexts/PreCheckInContext";
import Property from "../../model/property";
import BookingInfoImage from "./BookingInfoImage";

const OtherBookDetail = () => {
  const {
    propertyUid,
    language,
    data: { property, guestService, presets, upcomingStays },
    components: { PreCheckInHeader, PreCheckInContainer },
    func: { clearLocalStorage },
  } = usePreCheckInContext();
  const history = useHistory();
  const { t } = useTranslation();
  useEffect(() => {
    const unloadCallback = (event) => {
      event.preventDefault();
      const histories = [...Property.getHistoryStack()];
      clearLocalStorage();
      Property.setHistoryStack(histories);
      event.returnValue = "";
      return "";
    };

    window.addEventListener("beforeunload", unloadCallback);
    return () => window.removeEventListener("beforeunload", unloadCallback);
  }, [clearLocalStorage]);

  const goToBookingDetail = useCallback(
    (bookingIndex) => {
      const booking =
        upcomingStays.length >= bookingIndex + 1 && upcomingStays[bookingIndex];
      history.push(
        `/pre-checkin/${propertyUid}/book-detail/${get(
          booking,
          "booking_id"
        )}?language=${language}`
      );
    },
    [upcomingStays, propertyUid, language, history]
  );

  const confirmationPage = (bookingIndex) => {
    const booking =
      upcomingStays.length >= bookingIndex + 1 && upcomingStays[bookingIndex];
    const bookingNumber = get(booking, "booking_id");
    history.push(
      `/pre-checkin/${propertyUid}/booking/${bookingNumber}/request-completed?language=${language}`
    );
  };

  useEffect(() => {
    if (upcomingStays.length === 1) {
      const historyStackItem = [...Property.getHistoryStack()];
      historyStackItem.splice(0, 1);
      Property.setHistoryStack(historyStackItem);
      goToBookingDetail(0);
    }
  }, [upcomingStays, goToBookingDetail]);

  return (
    <>
      <PreCheckInContainer containerName="guest-list-management-book-detail">
        <PreCheckInHeader
          title={guestService?.title || t("PRE_CHECK_IN", "PRE CHECK-IN")}
          headerLink
        />
        {!isEmpty(guestService) && (
          <>
            <section
              id="content"
              className="relative flex flex-col px-9 w-full flex-1 md:flex-0 uppercase">
              <div className="flex flex-col justify-center h-full">
                {guestService.bookingInfoImage && (
                  <div className="-mx-9">
                    <BookingInfoImage
                      bookingInfoImage={guestService.bookingInfoImage}
                      propertyName={property.name}
                    />
                  </div>
                )}
                <div className="flex-1 w-full flex flex-col mt-6">
                  <div className="px-9 w-full flex flex-col md:h-auto">
                    <div
                      className="uppercase w-full md:text-center mb-2 type-title large"
                      style={{ color: presets.titleColor }}>
                      {guestService.upcomingStayTitle}
                    </div>
                    <p
                      className="w-full md:text-center capitalize leading-1 pt-2 mb-10 type-title medium"
                      style={{ color: presets.subtitleColor }}>
                      {guestService.upcomingStayDescription}
                    </p>
                    <div className="md:max-w-7xl mx-auto w-full mb-4">
                      {upcomingStays.map((elementInArray, index) => (
                        <div
                          className="w-full mb-16 rounded-3xl p-8 shadow-xl relative"
                          key={index}
                          style={{
                            backgroundColor: presets.backgroundColorTopBar,
                            height: "250px",
                          }}>
                          <div className="flex flex-col">
                            <p
                              className="text-center type-title normal"
                              style={{
                                color: presets.appHomeTextDetailsPrecheckin,
                              }}>
                              {property.name}
                            </p>
                            <div className="flex justify-between text-12 py-10">
                              <p
                                className="type-title normal"
                                style={{
                                  color: presets.appHomeTextDetailsPrecheckin,
                                }}>
                                {t("DATE")}
                              </p>
                              <p
                                className="type-regular normal"
                                style={{
                                  color: presets.appHomeTextDetailsPrecheckin,
                                }}>
                                {elementInArray.checkInDateText} -{" "}
                                {elementInArray.checkOutDateText}
                              </p>
                            </div>
                            <hr style={{ backgroundColor: "#DBDBDB" }} />
                            <div className="flex justify-between text-12 py-10">
                              <p
                                className="type-title normal"
                                style={{
                                  color: presets.appHomeTextDetailsPrecheckin,
                                }}>
                                {t("BOOKING_NO", "BOOKING NO.")}
                              </p>
                              <p
                                className="type-regular normal"
                                style={{
                                  color: presets.appHomeTextDetailsPrecheckin,
                                }}>
                                {elementInArray.booking_id}
                              </p>
                            </div>
                            <hr style={{ backgroundColor: "#DBDBDB" }} />
                            <div className="flex justify-between text-12 py-10">
                              <p
                                className="type-title normal"
                                style={{
                                  color: presets.appHomeTextDetailsPrecheckin,
                                }}>
                                {t("ROOM_TYPE", "ROOM TYPE")}
                              </p>
                              <p
                                className="type-regular normal"
                                style={{
                                  color: presets.appHomeTextDetailsPrecheckin,
                                }}>
                                {elementInArray.room_description}
                              </p>
                            </div>
                          </div>
                          {elementInArray.isBookingActive ? (
                            <div className="text-center">
                              <button
                                className={`bg-white app-outline-button type-title medium ${presets.buttonStyleName}`}
                                style={{
                                  color: presets.backgroundColorTopBar,
                                  borderColor: presets.backgroundColorTopBar,
                                }}
                                onClick={() => goToBookingDetail(index)}>
                                {t("READY_CHECK_IN")}
                              </button>
                            </div>
                          ) : (
                            <>
                              {[
                                "pre_check_in_completed",
                                "pre_checkin_completed",
                              ].includes(elementInArray.status) && (
                                <div className="text-center">
                                  <button
                                    className={`bg-white app-outline-button type-title medium ${presets.buttonStyleName}`}
                                    style={{
                                      color: presets.backgroundColorTopBar,
                                      borderColor:
                                        presets.backgroundColorTopBar,
                                    }}
                                    onClick={() => confirmationPage(index)}>
                                    {t("CHECK_IN_COMPLETED")}
                                  </button>
                                </div>
                              )}
                              {elementInArray.status === "room_is_ready" && (
                                <div className="text-center mx-auto">
                                  <button
                                    className={`bg-white app-outline-button type-title medium ${presets.buttonStyleName}`}
                                    style={{
                                      color: presets.backgroundColorTopBar,
                                      borderColor:
                                        presets.backgroundColorTopBar,
                                    }}
                                    onClick={() => confirmationPage(index)}>
                                    {t("ROOM_IS_READY")}
                                  </button>
                                </div>
                              )}
                            </>
                          )}
                        </div>
                      ))}
                    </div>
                    <button
                      className={`py-5 uppercase text-white type-title medium max-w-sm text-center mx-auto w-full hidden md:block mb-4 ${presets.buttonStyleName}`}
                      style={{
                        backgroundColor: presets.backgroundColorTopBar,
                        color: presets.appHomeTextCallToAction,
                      }}
                      onClick={() =>
                        history.push(
                          `/pre-checkin/${propertyUid}/home/?language=${language}`
                        )
                      }>
                      {t("ADD_RESERVATION")}
                    </button>
                  </div>
                </div>
              </div>
            </section>
          </>
        )}
      </PreCheckInContainer>
    </>
  );
};
export default withTranslation()(OtherBookDetail);
